export function toAmount(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function inscriptionResteAPayer(ins) {
  return Math.max(0, toAmount(ins?.montantTotalCdf) - toAmount(ins?.montantPayeCdf));
}

export function ligneFraisResteAPayer(ligne) {
  return Math.max(0, toAmount(ligne?.montantCdf) - toAmount(ligne?.montantPayeCdf));
}

/** Reste à payer sur la ligne INSCRIPTION (frais de souscription à la formation). */
export function inscriptionFraisInscriptionDue(ins) {
  const ligne = (ins?.lignesFrais ?? []).find((l) => l.typeFrais === 'INSCRIPTION');
  if (ligne) {
    return ligneFraisResteAPayer(ligne);
  }
  return 0;
}

/** Frais de souscription (ligne INSCRIPTION) entièrement réglés. */
export function inscriptionFraisInscriptionPaid(ins) {
  const statut = ins?.statut;
  if (statut === 'ANNULEE' || statut === 'DESISTEE' || statut === 'BROUILLON') {
    return false;
  }
  const ligne = (ins?.lignesFrais ?? []).find((l) => l.typeFrais === 'INSCRIPTION');
  if (ligne) {
    return ligneFraisResteAPayer(ligne) <= 0.01;
  }
  return (
    statut === 'CONFIRMEE' ||
    statut === 'EN_COURS' ||
    statut === 'TERMINEE' ||
    statut === 'DELIBEREE' ||
    statut === 'BREVET_DELIVRE'
  );
}

/** Inscrit à la formation : souscription payée (pas seulement une demande en attente). */
export function isInscriptionEffectivelyEnrolled(ins) {
  return inscriptionFraisInscriptionPaid(ins);
}

export function isInscriptionPendingSubscriptionPayment(ins) {
  const statut = ins?.statut;
  if (statut === 'ANNULEE' || statut === 'DESISTEE' || statut === 'BROUILLON') {
    return false;
  }
  return !inscriptionFraisInscriptionPaid(ins);
}

export function buildCohorteInscriptionIndex(inscriptions) {
  const enrolledByCohorteId = new Map();
  const pendingPaymentByCohorteId = new Map();

  for (const ins of inscriptions ?? []) {
    const cohorteId = ins?.idCohorte || ins?.cohorte?.id;
    if (!cohorteId || !ins?.id) continue;
    if (isInscriptionEffectivelyEnrolled(ins)) {
      enrolledByCohorteId.set(cohorteId, ins.id);
    } else if (isInscriptionPendingSubscriptionPayment(ins)) {
      pendingPaymentByCohorteId.set(cohorteId, ins.id);
    }
  }

  return { enrolledByCohorteId, pendingPaymentByCohorteId };
}

export function computeMesCoursEnrollmentStats(inscriptions) {
  let enrolled = 0;
  let pendingSubscription = 0;
  let actifs = 0;

  for (const row of inscriptions ?? []) {
    if (isInscriptionPendingSubscriptionPayment(row)) {
      pendingSubscription += 1;
      continue;
    }
    if (!isInscriptionEffectivelyEnrolled(row)) continue;

    enrolled += 1;
    if (
      row.statut === 'CONFIRMEE' ||
      row.statut === 'EN_COURS' ||
      row.statut === 'TERMINEE' ||
      row.statut === 'DELIBEREE' ||
      row.statut === 'BREVET_DELIVRE'
    ) {
      actifs += 1;
    }
  }

  return { total: enrolled, actifs, enAttente: pendingSubscription };
}

/**
 * État de navigation vers l'écran paiement.
 * @param {object} ins
 * @param {{ choice?: 'inscription' | 'total' | 'advance', customAmount?: number, filiereLabel?: string }} [options]
 * @returns {{
 *   montant: number,
 *   maxAmount: number,
 *   focusInscription: boolean,
 *   allowCustomAmount: boolean,
 *   paymentChoice: 'inscription' | 'total' | 'advance',
 *   filiereLabel?: string,
 * } | null}
 */
export function buildInscriptionPaymentState(ins, options = {}) {
  const choice = options.choice ?? 'total';
  const reste = inscriptionResteAPayer(ins);
  const fraisInscription = inscriptionFraisInscriptionDue(ins);
  const label = options.filiereLabel || ins?.filiere?.libelle || '';

  if (reste <= 0.01) {
    return null;
  }

  if (choice === 'inscription') {
    if (fraisInscription <= 0.01) {
      return null;
    }
    return {
      montant: fraisInscription,
      maxAmount: reste,
      focusInscription: true,
      allowCustomAmount: false,
      paymentChoice: 'inscription',
      filiereLabel: label,
    };
  }

  if (choice === 'advance') {
    const amt = toAmount(options.customAmount);
    if (amt <= 0 || amt > reste + 0.01) {
      return null;
    }
    return {
      montant: amt,
      maxAmount: reste,
      focusInscription: false,
      allowCustomAmount: true,
      paymentChoice: 'advance',
      filiereLabel: label,
    };
  }

  return {
    montant: reste,
    maxAmount: reste,
    focusInscription: false,
    allowCustomAmount: false,
    paymentChoice: 'total',
    filiereLabel: label,
  };
}

/** Raccourci : frais d'inscription d'abord, sinon solde total. */
export function buildInscriptionPaymentNavigationState(ins, filiereLabel = '') {
  const fraisInscription = inscriptionFraisInscriptionDue(ins);
  if (fraisInscription > 0.01) {
    return buildInscriptionPaymentState(ins, { choice: 'inscription', filiereLabel });
  }
  return buildInscriptionPaymentState(ins, { choice: 'total', filiereLabel });
}

export function formatInscriptionStatut(statut) {
  switch (statut) {
    case 'EN_ATTENTE_PAIEMENT':
      return 'En attente de paiement';
    case 'PARTIELLEMENT_PAYEE':
      return 'Paiement partiel';
    case 'CONFIRMEE':
      return 'Confirmée';
    case 'ANNULEE':
      return 'Annulée';
    default:
      return statut ?? '—';
  }
}

export function formatTransactionStatut(statut) {
  switch (statut) {
    case 'VALIDEE':
      return 'Validée';
    case 'ECHOUEE':
      return 'Échouée';
    case 'EN_ATTENTE':
      return 'En attente';
    case 'ANNULEE':
      return 'Annulée';
    default:
      return statut ?? '—';
  }
}
